import { Timestamp } from 'firebase/firestore';

/** Fixed category slugs in display order */
export const APP_CATEGORIES = ['core', 'productivity', 'community', 'experimental'] as const;
export type AppCategory = typeof APP_CATEGORIES[number];

export const APP_CATEGORY_LABELS: Record<AppCategory, string> = {
  core: 'Core',
  productivity: 'Productivity',
  community: 'Community',
  experimental: 'Experimental',
};

export type AppType = 'internal' | 'external';
export type AppStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested';
export type AppVisibility = 'global' | 'specific';

/** Firestore document shape for the `apps` collection */
export interface AppRegistryEntry {
  id: string;
  name: string;
  description: string;
  url: string; // internal path (e.g. "/plms") or external URL (https://...)
  type: AppType;
  category: AppCategory;
  status: AppStatus;

  // Visual
  faviconUrl: string | null;
  screenshotUrl: string | null;
  iconColor: string; // hex color for the card accent
  customIconUrl: string | null;

  // Metadata (auto-enriched from URL)
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;

  // Submission
  submittedBy: string; // user email
  submittedAt: Timestamp | Date;

  // Approval
  approvedBy: string | null;
  approvedAt: Timestamp | Date | null;
  adminNotes: string | null;

  // Visibility
  visibility: AppVisibility;
  allowedUsers: string[]; // emails, only used if visibility === 'specific'

  // Admin global hide — hides from ALL users' dashboards
  globallyHidden: boolean;

  // Internal app metrics (only for type === 'internal')
  metricsEnabled: boolean;
  metricsConfig: Record<string, unknown> | null;
}

/** Shape for creating a new submission (subset of fields) */
export interface AppSubmission {
  name: string;
  description: string;
  url: string;
  category: AppCategory;
  faviconUrl: string | null;
  screenshotUrl: string | null;
  iconColor: string;
  customIconUrl: string | null;
}

/** Response from the enrich-url function */
export interface EnrichUrlResponse {
  name: string | null;
  description: string | null;
  faviconUrl: string | null;
  screenshotUrl: string | null;
  ogImage: string | null;
  suggestedColor: string | null;
}
